/**
 * Argentina Bio Template - Specialized Bio Link Template for Argentina
 * Features province-specific activation management and Argentine market optimization
 * Uses Cloudflare banner and sophisticated design with province-based content
 */

import { BRAND_PERSON_NAME, BRAND_INSTAGRAM_URL } from "@/lib/brandConfig";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useArgentinaProvince } from "@/hooks/useArgentinaProvince";
import { useArgentinaBioTemplate } from "@/hooks/useArgentinaBioTemplate";
import { useTemplateTracking } from "@/hooks/useCombinedTracking";
// Amplitude tracking removed to prevent React error #310
import { trackTemplateUsageGA } from "@/lib/analytics";
import {
  trackTemplateUsagePostHog,
  trackButtonClickPostHog,
  trackSocialClickPostHog,
  trackConversionPostHog
} from "@/lib/posthogAnalytics";
// BioLink conversion tracking removed
// Background images removed - using pure black background
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
  trackInstagramBrowserUsage
} from "@/lib/instagramBrowserDetection";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Instagram } from "lucide-react";

// Lazy load heavy components to improve initial load time
const MuxVideoSurface = lazy(() => import("@/components/MuxVideoSurface"));
const PaymentModal = lazy(() => import("@/components/PaymentModal").then(module => ({ default: module.PaymentModal })));

// Lazy load all icons to reduce initial bundle size
const ChevronLeft = lazy(() => import("lucide-react").then(module => ({ default: module.ChevronLeft })));
const ChevronRight = lazy(() => import("lucide-react").then(module => ({ default: module.ChevronRight })));
const X = lazy(() => import("lucide-react").then(module => ({ default: module.X })));

// Import payment modal tracking
import { trackPaymentModalGA } from "@/lib/analytics";
import type { VideoActionButton } from "@/components/MuxVideoPlayer";

export interface TemplateProps {
  mediaItems: Array<{
    id: number;
    type: "video" | "photo";
    src: string;
    title: string;
    playbackId?: string;  // Mux playback ID (replaces cloudflareId)
    cloudflareId?: string;  // Legacy support - will be converted to playbackId
    embedId?: string;
    actionButton?: VideoActionButton;  // Optional: URL de destino para el botón del video
  }>;
}

export const ArgentinaBioTemplate: React.FC<TemplateProps> = ({ mediaItems }) => {
  const { t } = useTranslation();
  const instagramOpts = getInstagramOptimizations();
  const navigate = useNavigate();

  // Argentina-specific hooks
  const { province, provinceName, isLoading: provinceLoading } = useArgentinaProvince("AR");
  const { templateConfig, isProvinceActive } = useArgentinaBioTemplate();

  // Combined tracking hook for unified GA + PostHog tracking
  const { trackTemplateLoad, trackTemplateButton, trackSocialClick } = useTemplateTracking("ArgentinaBioTemplate");

  // Defer heavy template config until after initial render
  const templateSelection = useTemplateSelection();

  // Defer device detection to avoid blocking initial render
  const [deviceInfo, setDeviceInfo] = useState<{ isMobile: boolean }>({ isMobile: false });

  // Countdown timer state - 7 hours real countdown
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  }>({ hours: 0, minutes: 0, seconds: 0, isExpired: false });

  // Optimized state initialization
  const [selectedMedia, setSelectedMedia] = useState<typeof mediaItems[0] | null>(null);
  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean;
    service: "instagram" | "telegram" | "talk" | "group_talk";
    title: string;
  }>({
    isOpen: false,
    service: "instagram",
    title: ""
  });

  // Performance refs
  const lightboxOpenTimeRef = useRef<number | null>(null);
  const modalOpenTimeRef = useRef<number | null>(null);
  const imageLoadTimeRef = useRef<Record<string, number>>({});
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  // Initialize 7-hour countdown timer
  useEffect(() => {
    const COUNTDOWN_DURATION = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
    const STORAGE_KEY = 'argentina_biolink_countdown_start';

    // Get or set countdown start time
    let startTime = localStorage.getItem(STORAGE_KEY);
    if (!startTime) {
      startTime = Date.now().toString();
      localStorage.setItem(STORAGE_KEY, startTime);
    }

    const updateCountdown = () => {
      const now = Date.now();
      const elapsed = now - parseInt(startTime!);
      const remaining = Math.max(0, COUNTDOWN_DURATION - elapsed);

      if (remaining === 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds, isExpired: false });
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // AGGRESSIVE: Force black background and continuously remove any background images from Cloudflare
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Specific Cloudflare image URLs to block (exact IDs from backgroundImages.ts)
    const blockedImageUrls = [
      '5722ef64-fd7d-420c-efc3-b91ea5a6bd00', // Mobile background
      '607db50b-e13b-49b8-60a7-58e1c419ea00', // Desktop background
      'imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5722ef64-fd7d-420c-efc3-b91ea5a6bd00', // Full mobile URL
      'imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/607db50b-e13b-49b8-60a7-58e1c419ea00', // Full desktop URL
      'imagedelivery.net/N5r9mtOgBIolYeX8w64oqg'
    ];

    // Block image loading at network level by intercepting and canceling requests
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0]?.toString() || '';
      if (blockedImageUrls.some(blocked => url.includes(blocked))) {
        return Promise.reject(new Error('Background image blocked'));
      }
      return originalFetch.apply(this, args);
    };

    // Note: We rely on CSS and DOM manipulation to block backgrounds
    // Intercepting Image constructor is complex and may break other functionality

    // Function to remove background images from an element
    const removeBackgroundFromElement = (el: HTMLElement) => {
      const computedStyle = window.getComputedStyle(el);
      const bgImage = computedStyle.backgroundImage;

      // Check if background image contains blocked URLs
      if (bgImage && bgImage !== 'none' && bgImage !== 'initial') {
        const hasBlockedUrl = blockedImageUrls.some(url => bgImage.includes(url));
        if (hasBlockedUrl || bgImage.includes('imagedelivery.net')) {
          el.style.setProperty('background-image', 'none', 'important');
          el.style.setProperty('background', '#000000', 'important');
          el.style.setProperty('background-color', '#000000', 'important');
        }
      }

      // Also check pseudo-elements
      ['::before', '::after'].forEach(pseudo => {
        try {
          const pseudoStyle = window.getComputedStyle(el, pseudo);
          const pseudoBgImage = pseudoStyle.backgroundImage;
          if (pseudoBgImage && pseudoBgImage !== 'none' && pseudoBgImage.includes('imagedelivery.net')) {
            // Create a style rule to override pseudo-element
            const styleId = `argentina-biolink-pseudo-${pseudo.replace(/::/g, '-')}`;
            let pseudoStyleEl = document.getElementById(styleId) as HTMLStyleElement;
            if (!pseudoStyleEl) {
              pseudoStyleEl = document.createElement('style');
              pseudoStyleEl.id = styleId;
              document.head.appendChild(pseudoStyleEl);
            }
            // Add rule for this element's pseudo-element
            const className = `argentina-biolink-no-bg-${Date.now()}`;
            el.classList.add(className);
            pseudoStyleEl.textContent += `.${className}${pseudo} { background-image: none !important; background: #000000 !important; }`;
          }
        } catch (e) {
          // Pseudo-element access may fail, ignore
        }
      });
    };

    // Function to scan and clean all elements
    const scanAndCleanAllElements = () => {
      // Force black background on body and html first
      document.body.style.setProperty('background-color', '#000000', 'important');
      document.body.style.setProperty('background-image', 'none', 'important');
      document.body.style.setProperty('background', '#000000', 'important');
      document.documentElement.style.setProperty('background-color', '#000000', 'important');
      document.documentElement.style.setProperty('background-image', 'none', 'important');
      document.documentElement.style.setProperty('background', '#000000', 'important');

      // Scan all elements
      const allElements = document.querySelectorAll('*');
      allElements.forEach((el) => {
        removeBackgroundFromElement(el as HTMLElement);
      });
    };

    // Add comprehensive style tag to force black background with !important
    const styleId = 'argentina-biolink-black-background';
    let style = document.getElementById(styleId) as HTMLStyleElement;

    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* CRITICAL: Block specific Cloudflare background images by URL */
        *[style*="5722ef64-fd7d-420c-efc3-b91ea5a6bd00"],
        *[style*="607db50b-e13b-49b8-60a7-58e1c419ea00"],
        *[style*="imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5722ef64"],
        *[style*="imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/607db50b"],
        *[style*="url(https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5722ef64"],
        *[style*="url(https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/607db50b"] {
          background-image: none !important;
          background: #000000 !important;
          background-color: #000000 !important;
        }

        html, body, #root, #root > * {
          background-color: #000000 !important;
          background-image: none !important;
          background: #000000 !important;
        }
        .argentina-biolink-black-bg,
        .argentina-biolink-black-bg *,
        [class*="argentina-biolink"],
        main,
        main *,
        section,
        section * {
          background-color: #000000 !important;
          background-image: none !important;
          background: #000000 !important;
        }
        /* Block ALL background images from Cloudflare */
        * {
          background-image: none !important;
        }
        *::before,
        *::after {
          background-image: none !important;
          background: #000000 !important;
        }
        /* CRITICAL: Ensure all 3 buttons are always visible */
        .argentina-bio-link-button,
        button.argentina-bio-link-button,
        button[class*="argentina-bio-link-button"] {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
          z-index: 10 !important;
          position: relative !important;
          pointer-events: auto !important;
        }
        /* Ensure button container is visible */
        div[class*="space-y"] button,
        .argentina-bio-link-button + .argentina-bio-link-button,
        button + button {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
        }
        /* Override any hidden states */
        button.hidden,
        button[hidden],
        .argentina-bio-link-button.hidden {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Initial cleanup
    scanAndCleanAllElements();

    // Use MutationObserver to catch dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            removeBackgroundFromElement(node as HTMLElement);
            // Also check children
            const children = (node as HTMLElement).querySelectorAll('*');
            children.forEach((child) => {
              removeBackgroundFromElement(child as HTMLElement);
            });
          }
        });
      });
      // Re-scan periodically to catch style changes
      scanAndCleanAllElements();
    });

    // Observe the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also run periodic cleanup to catch any missed elements
    const cleanupInterval = setInterval(() => {
      scanAndCleanAllElements();

      // Ensure all 3 buttons are always visible
      const buttons = document.querySelectorAll('.argentina-bio-link-button, button.argentina-bio-link-button');
      buttons.forEach((btn) => {
        const htmlBtn = btn as HTMLElement;
        htmlBtn.style.setProperty('visibility', 'visible', 'important');
        htmlBtn.style.setProperty('opacity', '1', 'important');
        htmlBtn.style.setProperty('display', 'block', 'important');
        htmlBtn.style.setProperty('z-index', '10', 'important');
        htmlBtn.style.setProperty('position', 'relative', 'important');
        htmlBtn.style.setProperty('pointer-events', 'auto', 'important');
        // Remove any hidden attributes
        htmlBtn.removeAttribute('hidden');
        htmlBtn.classList.remove('hidden');
      });
    }, 100); // Check every 100ms

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      clearInterval(cleanupInterval);
      // Restore original fetch
      window.fetch = originalFetch;
      if (typeof document !== 'undefined') {
        const styleEl = document.getElementById(styleId);
        if (styleEl) {
          styleEl.remove();
        }
        // Remove pseudo-element styles
        document.querySelectorAll('[id^="argentina-biolink-pseudo-"]').forEach(el => el.remove());
      }
    };
  }, []);

  // CRITICAL: Ensure all 3 buttons are always visible
  useEffect(() => {
    const ensureButtonsVisible = () => {
      if (typeof document === 'undefined') return;

      const buttons = document.querySelectorAll('.argentina-bio-link-button, button.argentina-bio-link-button');
      buttons.forEach((btn) => {
        const htmlBtn = btn as HTMLElement;
        // Force visibility
        htmlBtn.style.setProperty('visibility', 'visible', 'important');
        htmlBtn.style.setProperty('opacity', '1', 'important');
        htmlBtn.style.setProperty('display', 'block', 'important');
        htmlBtn.style.setProperty('z-index', '10', 'important');
        htmlBtn.style.setProperty('position', 'relative', 'important');
        htmlBtn.style.setProperty('pointer-events', 'auto', 'important');
        // Remove hidden attributes
        htmlBtn.removeAttribute('hidden');
        htmlBtn.classList.remove('hidden');
      });
    };

    // Run immediately
    ensureButtonsVisible();

    // Run after a short delay to catch any late-rendered buttons
    const timeout = setTimeout(ensureButtonsVisible, 100);

    // Also run on any DOM changes
    const observer = new MutationObserver(() => {
      ensureButtonsVisible();
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    }

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, []);

  // Defer device detection to avoid blocking initial render
  useEffect(() => {
    // Use requestIdleCallback for non-critical detection
    const detectDevice = () => {
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setDeviceInfo({ isMobile });
      }
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(detectDevice, { timeout: 1000 });
    } else {
      setTimeout(detectDevice, 0);
    }
  }, []);

  // Social links - Instagram and YouTube removed for Argentina focus
  const socialLinks = [];

  // Navigation functions for lightbox
  const navigateToPrevious = () => {
    if (!selectedMedia) return;
    const currentIndex = mediaItems.findIndex((item) => item.id === selectedMedia.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
    const previousItem = mediaItems[previousIndex];

    // Amplitude tracking removed
    setSelectedMedia(previousItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  const navigateToNext = () => {
    if (!selectedMedia) return;
    const currentIndex = mediaItems.findIndex((item) => item.id === selectedMedia.id);
    const nextIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
    const nextItem = mediaItems[nextIndex];

    // Amplitude tracking removed
    setSelectedMedia(nextItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  // Swipe gesture state for Instagram-like navigation
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const SWIPE_THRESHOLD = 50; // Minimum distance for swipe
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity for swipe

  // Defer analytics tracking to avoid blocking initial render
  useEffect(() => {
    // Use requestIdleCallback for non-critical analytics
    const trackAnalytics = () => {
      // Track template load with detailed layout information
      const sectionName = "Argentina Bio Link";
      const layoutName = "argentina_bio_link";

      // Apply Instagram browser optimizations
      applyInstagramBrowserCSS();

      // Track Instagram browser usage
      trackInstagramBrowserUsage("ArgentinaBioTemplate", "AR");

      // Unified template tracking (GA + PostHog)
      trackTemplateLoad("AR", [
        "argentina_bio_link",
        "province_specific",
        "payment_modal",
        "tally_form",
        "cloudflare_images",
        "mobile_optimized",
        ...(province ? [`province_${province}`] : []),
        ...(templateConfig.country === "AR" ? ["argentina_market"] : [])
      ]);

      // Enhanced country tracking for Argentina template
      const timezone = templateSelection.timezone;
      const browserLang = templateSelection.browserLang;
      const countryCode = "AR";

      // Track template load with country information
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "argentina_template_load", {
          event_category: "Argentina Template Usage",
          event_label: `ArgentinaBioTemplate_AR_${province || 'unknown'}`,
          template_name: "ArgentinaBioTemplate",
          country_code: countryCode,
          province_code: province,
          province_name: provinceName,
          region: "Argentina",
          timezone: timezone,
          language: browserLang,
          is_argentina_bio_template: true,
          province_active: isProvinceActive(province),
        });
      }
    };

    // Defer analytics to idle time
    if (window.requestIdleCallback) {
      window.requestIdleCallback(trackAnalytics, { timeout: 2000 });
    } else {
      setTimeout(trackAnalytics, 100);
    }

    // Track time on page when user leaves
    const startTime = Date.now();

    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      // Amplitude tracking removed
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    // Defer scroll tracking to avoid blocking main thread
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
        // Amplitude scroll tracking removed
      }, 100); // Throttle scroll events
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [templateSelection, province, provinceName, isProvinceActive]);

  // Exit intent tracking
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        // Amplitude tracking removed
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  // Add performance optimizations
  useEffect(() => {
    // Polyfill for requestIdleCallback
    if (!window.requestIdleCallback) {
      window.requestIdleCallback = (callback: IdleRequestCallback) => {
        const timeoutId = setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 50 }), 1);
        return timeoutId as unknown as number;
      };
    }

    // Preload critical profile image immediately
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = HEADER_PROFILE_IMAGE_URL;
    preloadLink.as = 'image';
    preloadLink.fetchPriority = 'high';
    document.head.appendChild(preloadLink);

    // Background images removed - using pure black background

    // Optimize Instagram browser performance
    if (instagramOpts.isInstagramBrowser) {
      document.body.classList.add('argentina-instagram-browser-optimized');
    }

    // Add mobile performance optimizations
    const style = document.createElement('style');
    style.textContent = `
      .argentina-bio-link-button {
        /* Optimized for performance - no transitions by default */
        transform: translateZ(0); /* Hardware acceleration */
        backface-visibility: hidden; /* Prevent flickering */
        will-change: auto; /* Let browser optimize */
      }
      .argentina-bio-link-button:active {
        transform: translateZ(0) scale(0.98); /* Simple scale on tap */
      }
      .argentina-instagram-browser-optimized {
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
      }
      .argentina-instagram-browser-optimized * {
        -webkit-tap-highlight-color: transparent;
      }
      /* Remove all transitions for maximum performance */
      .argentina-bio-link-button * {
        transition: none !important;
      }
      /* Optimize for mobile performance */
      @media (max-width: 768px) {
        .argentina-bio-link-button {
          -webkit-transform: translateZ(0);
          -webkit-backface-visibility: hidden;
          -webkit-perspective: 1000;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      document.body.classList.remove('argentina-instagram-browser-optimized');
    };
  }, [instagramOpts.isInstagramBrowser]);

  // CRITICAL: Check if province is active - if not, show maintenance message
  if (province && !isProvinceActive(province)) {
    return (
      <div className="min-h-screen bg-black text-white font-body flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="text-6xl">🇦🇷</div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            {t("argentinaBio.maintenance.title", {
              province: provinceName || t("argentinaBio.countryArgentina"),
            })}
          </h1>
          <p className="text-gray-400 leading-relaxed">
            {t("argentinaBio.maintenance.body", {
              province: provinceName || t("argentinaBio.countryArgentina"),
            })}
          </p>
          <div className="font-body text-sm text-gray-500">
            {t("argentinaBio.maintenance.detected", {
              name: provinceName ?? "—",
              code: province ?? "—",
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen text-white bg-black font-body argentina-biolink-black-bg ${instagramOpts.cssClasses.container}`}
      style={{
        backgroundImage: 'none',
        backgroundColor: '#000000',
        background: '#000000'
      }}
    >
      <div className="fixed top-[max(0.5rem,env(safe-area-inset-top))] right-[max(0.5rem,env(safe-area-inset-right))] z-[100]">
        <LanguageSwitcher variant="dark" />
      </div>

      {/* Minimalist Elegant Argentina Bio Link Layout */}
      <main className="relative bg-black argentina-biolink-black-bg" role="main" style={{ backgroundColor: '#000000', backgroundImage: 'none' }}>
        {/* Bio Link Content - Clean, no background */}
        <div
          className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16 pb-32 bg-black argentina-biolink-black-bg"
          style={{ backgroundColor: '#000000', backgroundImage: 'none' }}
        >
          {/* Content - Ultra minimal */}
          <div className="relative z-10 w-full max-w-sm sm:max-w-md mx-auto text-center space-y-8 sm:space-y-10">

              {/* Profile Section */}
              <div className="space-y-4 sm:space-y-6">
                {/* Profile Image - Cloudflare Hosted */}
                <div className="relative mx-auto">
                  <div className="bio-profile w-24 h-24 sm:w-28 sm:h-28 mx-auto rounded-full overflow-hidden">
                    <div className="w-full h-full rounded-full overflow-hidden">
                      <img
                        src={HEADER_PROFILE_IMAGE_URL}
                        alt={t("argentinaBio.profileAlt")}
                        className="w-full h-full object-cover rounded-full"
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width="128"
                        height="128"
                        onLoad={(e) => {
                          const loadTime = performance.now() - (imageLoadTimeRef.current['profile'] || 0);
                          // Amplitude tracking removed
                        }}
                        onLoadStart={() => {
                          imageLoadTimeRef.current['profile'] = performance.now();
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.display = 'none';
                          const fallback = target.parentElement?.querySelector('.fallback-initials');
                          if (fallback) {
                            (fallback as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                      {/* Fallback initials */}
                      <div className="fallback-initials w-full h-full hidden items-center justify-center">
                        <span className="font-display text-3xl font-semibold text-white">DO</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Name & Badge */}
                <div className="space-y-2 sm:space-y-3">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight">
                    {BRAND_PERSON_NAME}
                  </h2>
                  <div className="inline-flex items-center px-4 py-1.5 border border-white/20 rounded-full">
                    <span className="font-body text-xs font-medium tracking-wide text-white/75">
                      {provinceName
                        ? t("argentinaBio.badgeWithProvince", {
                            province: provinceName,
                          })
                        : t("argentinaBio.badge")}
                    </span>
                  </div>
                </div>

                {/* Page Title */}
                <h1 className="sr-only">{t("argentinaBio.srOnlyH1")}</h1>

                {/* Province-specific countdown */}
                <div className="max-w-sm mx-auto px-4 sm:px-0">
                  <div className="bg-gradient-to-r from-blue-900/30 via-blue-800/40 to-blue-900/30 border border-blue-500/30 rounded-2xl p-6 backdrop-blur-sm">
                    <div className="text-center mb-4">
                      <h3 className="font-display text-lg sm:text-xl font-bold text-blue-300 mb-2 tracking-tight">
                        {t("argentinaBio.offerTitle", {
                          region:
                            provinceName || t("argentinaBio.countryArgentina"),
                        })}
                      </h3>
                      <p className="font-body text-blue-200/80 text-sm leading-relaxed">
                        {t("argentinaBio.offerSubtitle")}
                      </p>
                    </div>

                    {!timeLeft.isExpired ? (
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-black/40 rounded-xl p-3 border border-blue-500/20">
                          <div className="text-2xl sm:text-3xl font-bold text-white lining-nums tabular-nums">
                            {timeLeft.hours.toString().padStart(2, '0')}
                          </div>
                          <div className="font-body text-xs text-blue-300 uppercase tracking-wider">
                            {t("argentinaBio.unitHours")}
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-xl p-3 border border-blue-500/20">
                          <div className="text-2xl sm:text-3xl font-bold text-white lining-nums tabular-nums">
                            {timeLeft.minutes.toString().padStart(2, '0')}
                          </div>
                          <div className="font-body text-xs text-blue-300 uppercase tracking-wider">
                            {t("argentinaBio.unitMin")}
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-xl p-3 border border-blue-500/20">
                          <div className="text-2xl sm:text-3xl font-bold text-white lining-nums tabular-nums">
                            {timeLeft.seconds.toString().padStart(2, '0')}
                          </div>
                          <div className="font-body text-xs text-blue-300 uppercase tracking-wider">
                            {t("argentinaBio.unitSec")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="font-display text-xl font-bold text-blue-400 mb-2 tracking-tight">
                          {t("argentinaBio.offerExpiredTitle")}
                        </div>
                        <p className="font-body text-blue-300/80 text-sm leading-relaxed">
                          {t("argentinaBio.offerExpiredBody")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="max-w-sm mx-auto px-2 sm:px-0">
                  <p className="font-body text-sm sm:text-base text-white/70 font-light leading-relaxed lining-nums tabular-nums">
                    {provinceName
                      ? t("argentinaBio.descriptionWithProvince", {
                          province: provinceName,
                        })
                      : t("argentinaBio.descriptionDefault")}
                  </p>
                </div>
              </div>

              {/* Main Action Buttons - Prioritized Order */}
              <div className={`space-y-3 sm:space-y-4 px-2 sm:px-0 ${instagramOpts.cssClasses.buttons}`}>
                {/* PRIMARY: Telegram Button - Join to my Group */}
                <button
                  type="button"
                  onMouseEnter={() => {
                    // Defer tracking to avoid blocking main thread
                    requestIdleCallback(() => {
                      const hoverText = "Únete a mi Grupo";
                      // trackHover("button", "telegram-button", hoverText); // Disabled for performance
                    });
                  }}
                  onFocus={() => {
                    // trackFocus("button", "telegram-button"); // Disabled for performance
                  }}
                  onClick={() => {
                    const buttonText = t("argentinaBio.ctaTelegram");
                    const trackingId = "argentina-telegram";
                    const telegramCountryCode = "AR";

                    // Track navigation
                    trackButtonClickGA(buttonText, trackingId, "telegram_navigation", "telegram", telegramCountryCode, "ArgentinaBioTemplate");
                    trackButtonClickPostHog(buttonText, trackingId, "telegram_navigation", "telegram", telegramCountryCode, "ArgentinaBioTemplate");
                    trackConversionPostHog("navigation", "telegram");

                    // Navigate to grupo-privado (channel_invite / Telegram Groups)
                    navigate('/grupo-privado');
                  }}
                  className="argentina-bio-link-button font-body w-full py-4 sm:py-5 px-6 bg-white text-black hover:bg-white/90 font-medium text-base sm:text-lg rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation transition-colors duration-200"
                  style={{
                    visibility: 'visible',
                    opacity: 1,
                    display: 'block',
                    zIndex: 10,
                    position: 'relative'
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    <span>{t("argentinaBio.ctaTelegramJoin")}</span>
                  </div>
                </button>

                {/* SECONDARY: Talk to Me Button */}
                <button
                  type="button"
                  onMouseEnter={() => {
                    // Defer tracking to avoid blocking main thread
                    requestIdleCallback(() => {
                      const hoverText = "Habla conmigo";
                      // trackHover("button", "talk-button", hoverText); // Disabled for performance
                    });
                  }}
                  onFocus={() => {
                    // trackFocus("button", "talk-button"); // Disabled for performance
                  }}
                  onClick={() => {
                    const buttonText = t("argentinaBio.ctaTalk");
                    const trackingId = "argentina-talk";
                    const modalTitle = t("argentinaBio.ctaTalkLabel");
                    const talkCountryCode = "AR";

                    // Amplitude tracking removed
                    trackButtonClickGA(buttonText, trackingId, "talk_modal_open", "talk", talkCountryCode, "ArgentinaBioTemplate");
                    // PostHog tracking
                    trackButtonClickPostHog(buttonText, trackingId, "talk_modal_open", "talk", talkCountryCode, "ArgentinaBioTemplate");
                    trackConversionPostHog("modal_open", "talk");
                    trackPaymentModalGA("open", "talk");

                    // Amplitude tracking removed
                    modalOpenTimeRef.current = Date.now();

                    setPaymentModal({
                      isOpen: true,
                      service: "talk",
                      title: modalTitle
                    });
                  }}
                  className="argentina-bio-link-button w-full py-4 sm:py-5 px-6 bg-transparent border-2 border-white/50 text-white hover:border-white hover:bg-white/10 font-medium text-base sm:text-lg rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation ui-transition duration-200"
                  style={{
                    visibility: 'visible',
                    opacity: 1,
                    display: 'block',
                    zIndex: 10,
                    position: 'relative'
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                    </svg>
                    <span>{t("argentinaBio.ctaTalkLabel")}</span>
                  </div>
                </button>

                {/* Instagram button removed */}

                {/* Join Group & Talk Button */}
                <button
                  type="button"
                  onMouseEnter={() => {
                    const hoverText = "Únete a mi Grupo y Habla conmigo";
                    // Amplitude tracking removed
                  }}
                  onFocus={() => {/* Amplitude tracking removed */}}
                  onClick={() => {
                    const buttonText = t("argentinaBio.ctaGroupTalk");
                    const trackingId = "argentina-group-talk";
                    const modalTitle = t("argentinaBio.ctaGroupTalkLabel");
                    const groupTalkCountryCode = "AR";

                    // Amplitude tracking removed
                    trackButtonClickGA(buttonText, trackingId, "group_talk_modal_open", "group_talk", groupTalkCountryCode, "ArgentinaBioTemplate");
                    // PostHog tracking
                    trackButtonClickPostHog(buttonText, trackingId, "group_talk_modal_open", "group_talk", groupTalkCountryCode, "ArgentinaBioTemplate");
                    trackConversionPostHog("modal_open", "group_talk");
                    trackPaymentModalGA("open", "group_talk");

                    // Amplitude tracking removed
                    modalOpenTimeRef.current = Date.now();

                    setPaymentModal({
                      isOpen: true,
                      service: "group_talk",
                      title: modalTitle
                    });
                  }}
                  className="argentina-bio-link-button w-full py-4 sm:py-5 px-4 sm:px-6 bg-emerald-500/20 border-2 border-emerald-400/50 text-white hover:bg-emerald-500/30 hover:border-emerald-400 active:bg-emerald-500/40 font-semibold rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 touch-manipulation ui-transition duration-200"
                  style={{
                    visibility: 'visible',
                    opacity: 1,
                    display: 'block',
                    zIndex: 10,
                    position: 'relative'
                  }}
                >
                  <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                    </svg>
                    <span className="text-base sm:text-lg font-medium">
                      {t("argentinaBio.ctaGroupTalkLabel")}
                    </span>
                  </div>
                </button>

              </div>

              {/* Social Links */}
              <div className="flex justify-center space-x-4 sm:space-x-6 pt-6 sm:pt-8">
                <a
                  href={BRAND_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    // Amplitude tracking removed
                    // Enhanced social tracking with country info
                    const socialCountryCode = "AR";
                    if (typeof window !== "undefined" && window.gtag) {
                      window.gtag("event", "social_click_country", {
                        event_category: "Social Media",
                        event_label: `instagram_AR`,
                        country_code: socialCountryCode,
                        platform: "instagram",
                        template: "ArgentinaBioTemplate",
                        link_url: BRAND_INSTAGRAM_URL,
                      });

                      // PostHog social tracking
                      trackSocialClickPostHog("instagram", BRAND_INSTAGRAM_URL);
                    }
                  }}
                  className="w-14 h-14 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/20 shadow-lg touch-manipulation"
                  aria-label={t("argentinaBio.instagramAria")}
                >
                  <Instagram className="w-6 h-6 sm:w-5 sm:h-5" strokeWidth={1.5} />
                </a>
              </div>

            </div>
          </div>

        {/* Elegant Footer */}
        <footer className="py-8 px-4 text-center bg-gray-900 border-t border-white/10">
          <div className="flex flex-col items-center gap-4">
            {/* Social Media Icons */}
            <div className="flex items-center justify-center gap-4">
              <a
                href={BRAND_INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/20 ui-transition-colors duration-200"
                aria-label={t("argentinaBio.instagramAria")}
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
            {/* Copyright */}
            <p className="text-xs text-white/40 font-light tracking-wide">
              {t("argentinaBio.footer", {
                province:
                  provinceName || t("argentinaBio.countryArgentina"),
              })}
            </p>
          </div>
        </footer>
      </main>

      {/* Lightbox Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => {
            if (selectedMedia && lightboxOpenTimeRef.current) {
              const timeSpent = Math.round((Date.now() - lightboxOpenTimeRef.current) / 1000);
              // trackLightboxClose removed(selectedMedia.type, selectedMedia.id, selectedMedia.title, timeSpent);
              lightboxOpenTimeRef.current = null;
            }
            setSelectedMedia(null);
          }}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={() => {
              if (selectedMedia && lightboxOpenTimeRef.current) {
                const timeSpent = Math.round((Date.now() - lightboxOpenTimeRef.current) / 1000);
                // Lightbox close tracking removed
                lightboxOpenTimeRef.current = null;
              }
              setSelectedMedia(null);
            }}
            className="absolute top-6 right-6 sm:top-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={t("baseTemplate.lightbox.close")}
          >
            <X className="w-7 h-7" strokeWidth={2} />
          </button>

          {/* Navigation Buttons */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateToPrevious();
            }}
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={t("baseTemplate.lightbox.previous")}
          >
            <ChevronLeft className="w-7 h-7" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateToNext();
            }}
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={t("baseTemplate.lightbox.next")}
          >
            <ChevronRight className="w-7 h-7" strokeWidth={2} />
          </button>

          {/* Content with Instagram-like swipe gestures */}
          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              // Only handle swipe if not clicking on video player or action button
              const target = e.target as HTMLElement;
              if (target.closest('mux-player') || target.closest('.action-button-container')) {
                return; // Let video player handle its own touch events
              }
              
              const touch = e.touches[0];
              swipeStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now()
              };
            }}
            onTouchMove={(e) => {
              // Prevent default scrolling during swipe
              if (swipeStartRef.current) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeStartRef.current) return;
              
              // Only handle swipe if not clicking on video player or action button
              const target = e.target as HTMLElement;
              if (target.closest('mux-player') || target.closest('.action-button-container')) {
                swipeStartRef.current = null;
                return; // Let video player handle its own touch events
              }
              
              const touch = e.changedTouches[0];
              const deltaX = touch.clientX - swipeStartRef.current.x;
              const deltaY = touch.clientY - swipeStartRef.current.y;
              const deltaTime = Date.now() - swipeStartRef.current.time;
              const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
              const velocity = distance / deltaTime;
              
              // Only process swipe if it's primarily horizontal and meets threshold
              if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD && velocity > SWIPE_VELOCITY_THRESHOLD) {
                if (deltaX > 0) {
                  // Swipe right - previous
                  navigateToPrevious();
                } else {
                  // Swipe left - next
                  navigateToNext();
                }
              }
              
              swipeStartRef.current = null;
            }}
          >
            {selectedMedia.type === "video" && (selectedMedia.playbackId || selectedMedia.cloudflareId) ? (
              <div className="relative w-full h-full flex items-center justify-center bg-black">
                <Suspense fallback={
                  <div className="w-full h-full flex items-center justify-center bg-black/20 rounded-lg">
                    <div className="text-white/60 text-sm">
                      {t("argentinaBio.videoLoading")}
                    </div>
                  </div>
                }>
                  <MuxVideoSurface
                    playbackId={selectedMedia.playbackId || selectedMedia.cloudflareId || ''}
                    isVisible={true}
                    displayMode="reel"
                    posterFitMode="smartcrop"
                    actionButton={selectedMedia.actionButton}
                  />
                </Suspense>
              </div>
            ) : (
              <img
                src={selectedMedia.src}
                alt={`${selectedMedia.title ? `${selectedMedia.title} — ` : ""}${
                  selectedMedia.type === "video"
                    ? t("baseTemplate.galleryAlt.videoSuffix")
                    : t("baseTemplate.galleryAlt.photoSuffix")
                }`}
                className="w-full h-full object-cover md:object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <Suspense fallback={null}>
        <PaymentModal
          isOpen={paymentModal.isOpen}
          onClose={() => {
            // BioLink Conversion Funnel Tracking - Modal Close
            if (modalOpenTimeRef.current) {
              const timeSpent = Math.round((Date.now() - modalOpenTimeRef.current) / 1000);
              // trackBioLinkModalClosed(paymentModal.service, timeSpent); // Disabled for performance
            }

            setPaymentModal({ ...paymentModal, isOpen: false });
          }}
          title={paymentModal.title}
          service={paymentModal.service}
          country="AR"
        />
      </Suspense>

    </div>
  );
};